import React from 'react';
import StandardInputText from '@/components/input/StandardInputText';
import { EditIcon } from '@/assets/icons/button';
import FeatureTrackingService from '@/services/analytics/feature-tracking-service';

interface InlineEditableTextProps {
  /**
   * Current text value
   */
  text: string;
  /**
   * Whether currently in edit mode
   */
  isEditing: boolean;
  /**
   * Edit input value
   */
  editValue: string;
  /**
   * Callback when edit button is clicked
   */
  onEditClick: (e: React.MouseEvent) => void;
  /**
   * Callback when input value changes
   */
  onChange: (value: string) => void;
  /**
   * Callback when input loses focus (save)
   */
  onBlur: () => void;
  /**
   * Callback for keyboard events
   */
  onKeyDown: (e: React.KeyboardEvent) => void;
  /**
   * Unique ID for the input
   */
  id: string;
  /**
   * Text element type (h1, h2, h3, p, span)
   */
  textElement?: 'h1' | 'h2' | 'h3' | 'p' | 'span';
  /**
   * CSS classes for text element
   */
  textClassName?: string;
  /**
   * Whether to show edit button. Defaults to true.
   */
  showEditButton?: boolean;
}

/**
 * Reusable inline editable text component with edit button.
 * Switches between display and edit modes for unit/subunit names.
 */
export default function InlineEditableText({
  text,
  isEditing,
  editValue,
  onEditClick,
  onChange,
  onBlur,
  onKeyDown,
  id,
  textElement = 'h3',
  textClassName = 'text-text_gray_900 self-stretch text-sm not-italic font-semibold leading-5',
  showEditButton = true,
}: InlineEditableTextProps) {
  const TextElement = textElement;

  const handleTrackedEditClick = (e: React.MouseEvent) => {
    FeatureTrackingService.captureEvent('inline_text_edit_started', {
      field_id: id,
      text_element: textElement,
    });
    onEditClick(e);
  };

  if (isEditing) {
    return (
      <StandardInputText
        label={''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={(e: any) => onKeyDown(e)}
        onClick={(e: any) => e.stopPropagation()}
        value={editValue}
        id={id}
        autoFocus
      />
    );
  }

  return (
    <div className='flex items-center gap-2'>
      <TextElement className={textClassName}>{text}</TextElement>
      {showEditButton && (
        <button
          onClick={handleTrackedEditClick}
          className='flex items-center justify-center p-1 rounded hover:bg-surface_gray_100 transition-colors'
        >
          <EditIcon className='w-3 h-3 text-text_gray_600' />
        </button>
      )}
    </div>
  );
}
